import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  SimpleButton,
  Select,
  DropdownMenu,
  Modal,
  Toast,
  useStateContext,
} from '@veda/dashboard-ui';
import {
  MdAdd, MdEdit, MdDelete, MdEmail, MdClose, MdCheckCircle,
  MdPending, MdAccountBalance, MdArrowBack,
} from 'react-icons/md';
import { BsBoxArrowUpRight, BsTrash } from 'react-icons/bs';
import { usePppApi } from '../services/pppApi';
import { useAuthUser } from '../hooks/useAuthUser';

const TIPOLOGIA_OPTIONS = [
  '', 'Comune', 'Provincia', 'Regione', 'Città Metropolitana',
  'ASL', 'Università', 'Ente Pubblico Non Economico', 'Altro',
];

// Ruoli assegnabili al destinatario di un invito (whitelist allineata al backend RuoloInvitabile)
const RUOLO_OPTIONS = [
  { value: 'amministratore_ente',    label: 'Amministratore ente' },
  { value: 'ufficio_tecnico',        label: 'Ufficio tecnico' },
  { value: 'ufficio_amministrativo', label: 'Ufficio amministrativo' },
  { value: 'dirigente',              label: 'Dirigente' },
];

function Badge({ children, color = 'gray' }) {
  const colors = {
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    gray:  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[color]}`}>{children}</span>;
}

function LabelValore({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-800 dark:text-white mt-0.5">{value || '—'}</p>
    </div>
  );
}

// ─── Form crea / modifica ente ────────────────────────────────────────────────
function FormEnte({ iniziale, onSalva, onAnnulla, saving, errors }) {
  const [form, setForm] = useState({
    denominazione:  iniziale?.denominazione  ?? '',
    codiceIpa:      iniziale?.codiceIpa      ?? '',
    tipologia:      iniziale?.tipologia      ?? '',
    comune:         iniziale?.comune         ?? '',
    provincia:      iniziale?.provincia      ?? '',
    cap:            iniziale?.cap            ?? '',
    indirizzo:      iniziale?.indirizzo      ?? '',
    pec:            iniziale?.pec            ?? '',
    sitoWeb:        iniziale?.sitoWeb        ?? '',
    referenteNome:  iniziale?.referenteNome  ?? '',
    referenteEmail: iniziale?.referenteEmail ?? '',
    attivo:         iniziale?.attivo ?? true,
  });

  const field = (label, key, extra = {}) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</label>
      <input
        value={form[key]}
        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
        className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        {...extra}
      />
      {errors?.[key] && <p className="text-xs text-red-500">{errors[key][0]}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      {field('Denominazione *', 'denominazione')}

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tipologia</label>
          <select
            value={form.tipologia}
            onChange={(e) => setForm((p) => ({ ...p, tipologia: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none"
          >
            {TIPOLOGIA_OPTIONS.map((t) => <option key={t} value={t}>{t || 'Non specificata'}</option>)}
          </select>
        </div>
        {field('Codice IPA', 'codiceIpa')}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {field('Comune', 'comune')}
        {field('Prov.', 'provincia', { maxLength: 2 })}
        {field('CAP', 'cap', { maxLength: 5 })}
      </div>

      {field('Indirizzo', 'indirizzo')}

      <div className="grid grid-cols-2 gap-3">
        {field('PEC', 'pec', { type: 'email' })}
        {field('Sito web', 'sitoWeb')}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {field('Nome referente', 'referenteNome')}
        {field('Email referente', 'referenteEmail', { type: 'email' })}
      </div>

      {iniziale && (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={form.attivo}
            onChange={(e) => setForm((p) => ({ ...p, attivo: e.target.checked }))}
            className="rounded"
          />
          Ente attivo
        </label>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onAnnulla} className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          Annulla
        </button>
        <button
          onClick={() => onSalva(form)}
          disabled={saving || !form.denominazione.trim()}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white transition-colors"
        >
          {saving ? 'Salvataggio...' : (iniziale ? 'Salva modifiche' : 'Crea ente')}
        </button>
      </div>
    </div>
  );
}

// ─── Pannello inviti per un ente ──────────────────────────────────────────────
function PannelloInviti({ ente, onChiudi }) {
  const api = usePppApi();
  const [inviti,   setInviti]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [email,    setEmail]    = useState('');
  const [ruolo,    setRuolo]    = useState('amministratore_ente');
  const [inviando, setInviando] = useState(false);
  const [msg,      setMsg]      = useState('');
  const { addToast } = useStateContext();

  const carica = useCallback(() => {
    setLoading(true);
    api.listInvitiEnte(ente.id, {
      onSuccess: (data) => { setInviti(data ?? []); setLoading(false); },
      onError:   () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ente.id]);

  useEffect(() => { carica(); }, [carica]);

  const handleInvita = () => {
    if (!email.trim()) return;
    setInviando(true);
    api.creaInvitoEnte(ente.id, { email, ruolo }, {
      onSuccess: () => {
        setEmail('');
        setMsg(`Invito inviato a ${email}`);
        setInviando(false);
        setTimeout(() => setMsg(''), 4000);
        carica();
      },
      onFormError: () => setInviando(false),
    });
  };

  const handleRevoca = (invito) => {
    api.revocaInvitoEnte(ente.id, invito.id, {
      onSuccess: () => {
        addToast(<Toast duration={3} content="Invito revocato." />);
        carica();
      },
    });
  };

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 mt-6 pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <MdEmail size={18} className="text-blue-500" />
          Inviti per {ente.denominazione}
        </h3>
        <button onClick={onChiudi} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <MdClose size={18} />
        </button>
      </div>

      {/* Form nuovo invito */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleInvita()}
          placeholder="email@ente.it"
          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="sm:w-56">
          <Select
            label="Ruolo"
            value={ruolo}
            onChange={(e) => setRuolo(e.target.value)}
            options={RUOLO_OPTIONS}
          />
        </div>
        <button
          onClick={handleInvita}
          disabled={inviando || !email.trim()}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white transition-colors"
        >
          <MdEmail size={16} /> {inviando ? 'Invio...' : 'Invita'}
        </button>
      </div>

      {msg && (
        <p className="text-sm text-green-600 dark:text-green-400 mb-3 flex items-center gap-1">
          <MdCheckCircle size={16} /> {msg}
        </p>
      )}

      {/* Lista inviti */}
      {loading ? (
        <p className="text-sm text-gray-400 py-4 text-center">Caricamento...</p>
      ) : inviti.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">Nessun invito attivo per questo ente.</p>
      ) : (
        <div className="space-y-2">
          {inviti.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600"
            >
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">{inv.email}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Scade: {new Date(inv.expiresAt).toLocaleDateString('it-IT')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {inv.valido
                  ? <Badge color="amber"><MdPending size={12} /> In attesa</Badge>
                  : <Badge color="gray">Scaduto</Badge>
                }
                {inv.valido && (
                  <button
                    onClick={() => handleRevoca(inv)}
                    className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Revoca invito"
                  >
                    <BsTrash size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function EntiList() {
  const api        = usePppApi();
  const { user, can } = useAuthUser();
  const navigate   = useNavigate();
  const { addToast, addModal } = useStateContext();

  // L'amministratore_ente non vede la lista di tutti gli enti — solo il proprio
  useEffect(() => {
    if (user?.role === 'amministratore_ente') {
      navigate('/pa/ente', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const [enti,          setEnti]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [formErrors,    setFormErrors]    = useState(null);
  const [showForm,      setShowForm]      = useState(false);   // crea nuovo
  const [editingEnte,   setEditingEnte]   = useState(null);    // modifica
  const [invitiEnte,    setInvitiEnte]    = useState(null);    // pannello inviti aperto

  const carica = useCallback(() => {
    setLoading(true);
    api.listEnti({
      onSuccess: (data) => { setEnti(data ?? []); setLoading(false); },
      onError:   () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const handleCrea = (form) => {
    setSaving(true);
    setFormErrors(null);
    api.creaEnte(form, {
      onSuccess: () => {
        setSaving(false);
        setShowForm(false);
        addToast(<Toast duration={4} content="Ente creato con successo." />);
        carica();
      },
      onFormError: (res) => { setSaving(false); setFormErrors(res.errors); },
    });
  };

  const handleAggiorna = (form) => {
    setSaving(true);
    setFormErrors(null);
    api.aggiornaEnte(editingEnte.id, form, {
      onSuccess: () => {
        setSaving(false);
        setEditingEnte(null);
        addToast(<Toast duration={4} content="Ente aggiornato." />);
        carica();
      },
      onFormError: (res) => { setSaving(false); setFormErrors(res.errors); },
    });
  };

  const apriModifica = (ente) => {
    setInvitiEnte(null);
    setShowForm(false);
    setFormErrors(null);
    setEditingEnte(ente);
  };

  const handleElimina = (ente) => {
    addModal(
      <Modal
        title={`Elimina ${ente.denominazione}`}
        content="Sei sicuro di voler eliminare questo ente? L'operazione è irreversibile."
        confirm="Elimina"
        style="danger"
        onClick={() => {
          api.eliminaEnte(ente.id, {
            onSuccess: () => {
              addToast(<Toast duration={4} content="Ente eliminato." />);
              carica();
            },
          });
        }}
      />
    );
  };

  // Colonne tabella
  const cols = [
    { name: 'action',       label: '',              size: 'w-10 max-w-[40px]', orderable: 0 },
    { name: 'denominazione', label: 'Denominazione' },
    { name: 'tipologia',    label: 'Tipologia',     size: 'w-36' },
    { name: 'comune',       label: 'Sede',          size: 'w-40' },
    { name: 'pec',          label: 'PEC',           size: 'w-56' },
    { name: 'attivo',       label: 'Stato',         size: 'w-24' },
  ];

  const menuItems = (ente) => [
    can('ppp.enti.view') && (
      <button
        key="inviti"
        onClick={() => { setEditingEnte(null); setShowForm(false); setInvitiEnte(ente); }}
        className="w-full flex items-center gap-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
      >
        <MdEmail size={16} />
        <span>Gestisci inviti</span>
      </button>
    ),
    can('ppp.enti.edit') && (
      <button
        key="modifica"
        onClick={() => apriModifica(ente)}
        className="w-full flex items-center gap-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
      >
        <MdEdit size={16} />
        <span>Modifica</span>
      </button>
    ),
    can('ppp.enti.delete') && (
      <button
        key="elimina"
        onClick={() => handleElimina(ente)}
        className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600"
      >
        <BsTrash size={14} />
        <span>Elimina</span>
      </button>
    ),
  ].filter(Boolean);

  const cells = {
    action: (row) => <DropdownMenu menu_items={menuItems(row)} />,
    denominazione: (row) => (
      <div className="flex items-center gap-2">
        <MdAccountBalance size={16} className="text-blue-400 shrink-0" />
        {can('ppp.enti.edit') ? (
          <button
            onClick={() => apriModifica(row)}
            title="Apri dettaglio ente"
            className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors text-left"
          >
            {row.denominazione}
          </button>
        ) : (
          <span className="font-medium text-gray-900 dark:text-white">{row.denominazione}</span>
        )}
        {row.codiceIpa && (
          <span className="text-xs text-gray-400 font-mono">{row.codiceIpa}</span>
        )}
      </div>
    ),
    tipologia: (row) => row.tipologia ?? '—',
    comune: (row) => [row.comune, row.provincia ? `(${row.provincia})` : null].filter(Boolean).join(' ') || '—',
    pec: (row) => <span className="text-xs text-gray-500 dark:text-gray-400">{row.pec ?? '—'}</span>,
    attivo: (row) => (
      row.attivo
        ? <Badge color="green">Attivo</Badge>
        : <Badge color="gray">Inattivo</Badge>
    ),
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Enti pubblici (PA)</h1>
            <p className="text-sm text-gray-500 mt-1">Anagrafica delle Pubbliche Amministrazioni registrate</p>
          </div>
          {can('ppp.enti.create') && !showForm && !editingEnte && (
            <SimpleButton
              customFunc={() => { setShowForm(true); setEditingEnte(null); setInvitiEnte(null); setFormErrors(null); }}
              text={<span className="flex items-center gap-1"><MdAdd size={16} /> Nuovo ente</span>}
            />
          )}
        </div>

        {/* Form crea nuovo ente */}
        {showForm && (
          <div className="mb-6 p-5 rounded-xl border border-blue-100 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10">
            <h2 className="font-semibold text-gray-800 dark:text-white mb-4">Nuovo ente</h2>
            <FormEnte
              onSalva={handleCrea}
              onAnnulla={() => { setShowForm(false); setFormErrors(null); }}
              saving={saving}
              errors={formErrors}
            />
          </div>
        )}

        {/* Form modifica ente */}
        {editingEnte && (
          <div className="mb-6 p-5 rounded-xl border border-amber-100 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-900/10">
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => { setEditingEnte(null); setFormErrors(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <MdArrowBack size={20} />
              </button>
              <h2 className="font-semibold text-gray-800 dark:text-white">
                Modifica — {editingEnte.denominazione}
              </h2>
            </div>
            <FormEnte
              iniziale={editingEnte}
              onSalva={handleAggiorna}
              onAnnulla={() => { setEditingEnte(null); setFormErrors(null); }}
              saving={saving}
              errors={formErrors}
            />
          </div>
        )}

        {/* Tabella enti */}
        <Table
          cols={cols}
          dataset={enti}
          cells_style={cells}
          loading={loading}
          empty_message="Nessun ente registrato. Crea il primo ente con il pulsante in alto a destra."
        />

        {/* Pannello inviti inline (sotto la tabella, per l'ente selezionato) */}
        {invitiEnte && (
          <PannelloInviti
            ente={invitiEnte}
            onChiudi={() => setInvitiEnte(null)}
          />
        )}

      </div>
    </div>
  );
}
